"use client";

import { createContext, useContext, useCallback, useEffect, useState, type ReactNode } from "react";

import { useAuth } from "@/contexts/AuthContext";
import type { PublicBrandDetails } from "@/lib/public-shop-agent-fetch";
import { type BrandDetails, getBrandDetails } from "@/lib/shop-agent-client";

interface BrandDetailsContextValue {
  brandDetails: BrandDetails | null;
  loading: boolean;
  hasError: boolean;
  refetch: () => Promise<void>;
}

const BrandDetailsContext = createContext<BrandDetailsContextValue | null>(null);

/**
 * Type for initial data that can be passed from server components.
 * Accepts either the full BrandDetails (from client fetch) or
 * PublicBrandDetails (from server-side fetch via public-shop-agent-fetch).
 */
type InitialBrandData = BrandDetails | PublicBrandDetails | null;

interface BrandDetailsProviderProps {
  brandName: string;
  children: ReactNode;
  /**
   * Optional initial brand details from server-side fetch.
   * When provided, the provider starts with this data (no loading state)
   * and may refresh full client details in the background when needed.
   * Accepts PublicBrandDetails from SSR or full BrandDetails from client.
   */
  initialData?: InitialBrandData;
}

/**
 * Provider that fetches brand details once and shares them with all child components.
 * Supports SSR by accepting initialData from server-side fetch.
 */
export function BrandDetailsProvider({
  brandName,
  children,
  initialData,
}: BrandDetailsProviderProps) {
  // Initialize with server-provided data if available
  // Cast to BrandDetails since PublicBrandDetails is a compatible subset
  const [brandDetails, setBrandDetails] = useState<BrandDetails | null>(
    (initialData as BrandDetails | null) ?? null
  );
  // If we have initial data, start with loading=false
  const [loading, setLoading] = useState(!initialData);
  const [hasError, setHasError] = useState(false);
  const { loading: authLoading } = useAuth();
  // Transitional rollout guard:
  // - New SSR payloads include the brand_articles key (possibly null), so we can skip client refetch.
  // - Older cached SSR payloads may not include this key, so we fetch once to hydrate full details.
  // After old SSR caches have expired post-deploy, this can be simplified.
  const hasHydratedArticles = Boolean(
    initialData &&
      typeof initialData === "object" &&
      Object.prototype.hasOwnProperty.call(initialData, "brand_articles")
  );

  const fetchBrandDetails = useCallback(
    async (backgroundRefresh: boolean = false) => {
      // Wait for auth state to settle before making API calls.
      // This ensures consistent behavior and allows the API client to include
      // auth headers if available (for analytics/personalization), even though
      // the brand details endpoint doesn't require authentication.
      if (authLoading) {
        return;
      }

      try {
        if (!backgroundRefresh) {
          setLoading(true);
          setHasError(false);
        }

        const result = await getBrandDetails(brandName);

        if (result) {
          setBrandDetails(result);
          setHasError(false);
          return;
        }

        // Keep server-provided data if a background refresh fails to return data.
        if (!backgroundRefresh) {
          setBrandDetails(null);
        }
      } catch (error) {
        console.error("Error fetching brand details:", error);
        // Preserve existing data on background refresh failures.
        if (!backgroundRefresh) {
          setHasError(true);
          setBrandDetails(null);
        }
      } finally {
        if (!backgroundRefresh) {
          setLoading(false);
        }
      }
    },
    [brandName, authLoading]
  );

  const refetch = useCallback(async () => {
    await fetchBrandDetails(false);
  }, [fetchBrandDetails]);

  useEffect(() => {
    // If SSR already includes brand_articles, skip the extra client fetch.
    // Otherwise, fetch full details (background when initialData exists).
    if (initialData && hasHydratedArticles) {
      return;
    }
    void fetchBrandDetails(Boolean(initialData));
  }, [fetchBrandDetails, hasHydratedArticles, initialData]);

  return (
    <BrandDetailsContext.Provider
      value={{
        brandDetails,
        loading: loading || authLoading, // Keep loading true while auth is initializing
        hasError,
        refetch,
      }}
    >
      {children}
    </BrandDetailsContext.Provider>
  );
}

/**
 * Hook to access brand details from context
 */
export function useBrandDetails() {
  const context = useContext(BrandDetailsContext);
  if (!context) {
    throw new Error("useBrandDetails must be used within a BrandDetailsProvider");
  }
  return context;
}
